// BOXX operator console — shared UI primitives.
//
// Every screen in the console is built out of this file. There is no component
// library: each primitive is a thin wrapper over the hand-rolled classes in
// styles.css, so the look of the product lives in one stylesheet and the
// behaviour lives here.
//
// Contracts other modules depend on, stated once:
//
//   * Dialog / Drawer / Menu render through createPortal into document.body, so
//     a table with `overflow-x: auto` can never clip them.
//   * Dialog and Drawer trap Tab, close on Escape (topmost overlay only) and
//     return focus to whatever opened them.
//   * <Confirm> calls onConfirm and closes ONLY when it resolves. A rejection
//     leaves the dialog open — the caller surfaces the error with a toast or a
//     Banner underneath.
//   * <Pager onChange> is handed the new OFFSET, not a page number, because the
//     props it takes are offset/limit.
//   * useAsync always reports an ApiError, even when the thrown value was not
//     one. Screens print `error.traceId` in a Banner and support correlates the
//     report with the server log from it, so that field must always exist.
//
// Toast state is module-level. <ToastProvider> exists for symmetry with the
// hook, but useToast() works with or without it — a screen that pushes a toast
// while the provider is somewhere else in the tree still reaches the same host.

import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DependencyList,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ApiError } from "./api";

// ============================================================ focus + overlays

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function focusableIn(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.getClientRects().length > 0
  );
}

// Only the topmost overlay reacts to Escape and traps Tab. A document-level
// listener alone would close the dialog *underneath* an inner one, because
// capture-phase order is registration order and the outer overlay registered
// first.
const overlayStack: symbol[] = [];

function useOverlay(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  // Held in a ref so the effect below does not re-run — and re-steal focus —
  // every time the parent re-renders with a fresh inline onClose.
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const token = Symbol("overlay");
    overlayStack.push(token);
    const isTop = () => overlayStack[overlayStack.length - 1] === token;

    const restoreTo = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e: KeyboardEvent) => {
      if (!isTop() || !ref.current) return;

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== "Tab") return;

      const items = focusableIn(ref.current);
      if (!items.length) {
        e.preventDefault();
        ref.current.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (!ref.current.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey, true);

    const frame = requestAnimationFrame(() => {
      const root = ref.current;
      if (!root) return;
      const preferred = root.querySelector<HTMLElement>("[data-autofocus]");
      (preferred ?? focusableIn(root)[0] ?? root).focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKey, true);
      const i = overlayStack.indexOf(token);
      if (i >= 0) overlayStack.splice(i, 1);
      document.body.style.overflow = prevOverflow;
      restoreTo?.focus?.();
    };
  }, [open]);

  return ref;
}

// ============================================================ Dialog

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** 880px instead of 560px. */
  wide?: boolean;
  /** 1200px and full height — for builders and side-by-side diffs. */
  full?: boolean;
  children?: ReactNode;
}

export function DialogFooter({ children }: { children?: ReactNode }) {
  return <div className="dialog-foot">{children}</div>;
}

export function Dialog({ open, onClose, title, wide, full, children }: DialogProps) {
  const ref = useOverlay(open, onClose);
  const labelId = useId();
  const reduce = useReducedMotion();

  // The footer is pinned below the scrolling body, so it is lifted out of
  // `children` instead of being left inline where it would scroll away.
  const kids = Children.toArray(children);
  const isFooter = (k: ReactNode) => isValidElement(k) && k.type === DialogFooter;
  const footer = kids.filter(isFooter);
  const body = kids.filter((k) => !isFooter(k));

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="scrim"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            ref={ref}
            className={`dialog${wide ? " wide" : ""}${full ? " full" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelId}
            tabIndex={-1}
            initial={reduce ? false : { opacity: 0, scale: 0.98, y: 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.98, y: 4 }}
            transition={{ duration: 0.14 }}
          >
            <div className="dialog-head">
              <h2 id={labelId}>{title}</h2>
              <button className="btn icon ghost" onClick={onClose} aria-label="Close dialog">
                ✕
              </button>
            </div>
            <div className="dialog-body">{body}</div>
            {footer}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

// ============================================================ Drawer

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children?: ReactNode;
}

export function Drawer({ open, onClose, title, children }: DrawerProps) {
  const ref = useOverlay(open, onClose);
  const labelId = useId();
  const reduce = useReducedMotion();

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            onMouseDown={onClose}
          />
          <motion.aside
            ref={ref}
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelId}
            tabIndex={-1}
            initial={reduce ? { opacity: 0 } : { x: 24, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { x: 24, opacity: 0 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
          >
            <div className="dialog-head">
              <h2 id={labelId}>{title}</h2>
              <button className="btn icon ghost" onClick={onClose} aria-label="Close panel">
                ✕
              </button>
            </div>
            <div className="dialog-body">{children}</div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

// ============================================================ Confirm

export interface ConfirmProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: ReactNode;
  body?: ReactNode;
  danger?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
}

/** Every destructive action goes through this. Nothing is deleted on one click. */
export function Confirm({
  open,
  onClose,
  onConfirm,
  title,
  body,
  danger,
  confirmLabel,
  cancelLabel = "Cancel",
}: ConfirmProps) {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } catch {
      // Left open on failure: the caller has shown the reason and the operator
      // can retry or back out.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <p>{body ?? "This cannot be undone."}</p>
      <DialogFooter>
        {/* Cancel takes initial focus so Enter on a destructive prompt is safe. */}
        <button className="btn" onClick={onClose} disabled={busy} data-autofocus>
          {cancelLabel}
        </button>
        <button className={`btn ${danger ? "danger" : "primary"}`} onClick={run} disabled={busy}>
          {busy && <span className="spinner" />}
          {confirmLabel ?? (danger ? "Delete" : "Confirm")}
        </button>
      </DialogFooter>
    </Dialog>
  );
}

// ============================================================ Menu

export interface MenuItemSpec {
  label?: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  /** Draw a divider above this row. An entry with no label is a divider alone. */
  sep?: boolean;
  disabled?: boolean;
}

export interface MenuProps {
  items: MenuItemSpec[];
  /** Any element; it is cloned to receive the click and the aria wiring. */
  trigger: ReactNode;
}

export function Menu({ items, trigger }: MenuProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const anchorRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const a = anchorRef.current?.getBoundingClientRect();
    if (a) setPos({ top: a.bottom + 4, left: a.right });
  }, []);

  // Flip above the trigger when there is no room below, and pull back inside the
  // viewport when the menu is wider than the space to the left of the anchor.
  useLayoutEffect(() => {
    if (!open) return;
    const m = menuRef.current?.getBoundingClientRect();
    const a = anchorRef.current?.getBoundingClientRect();
    if (!m || !a) return;
    let top = a.bottom + 4;
    if (top + m.height > window.innerHeight - 8) top = Math.max(8, a.top - m.height - 4);
    let left = a.right;
    if (left - m.width < 8) left = Math.min(window.innerWidth - 8, a.left + m.width);
    setPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onMove = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    const frame = requestAnimationFrame(() => focusableIn(menuRef.current)[0]?.focus());
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open]);

  const toggle = () => {
    place();
    setOpen((v) => !v);
  };

  const triggerNode = isValidElement(trigger) ? (
    cloneElement(trigger as ReactElement<Record<string, unknown>>, {
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        (trigger.props as { onClick?: (e: React.MouseEvent) => void }).onClick?.(e);
        toggle();
      },
      "aria-haspopup": "menu",
      "aria-expanded": open,
    })
  ) : (
    <button
      className="btn icon ghost"
      onClick={toggle}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label="More actions"
    >
      {trigger ?? "⋯"}
    </button>
  );

  const onMenuKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      setOpen(false);
      focusableIn(anchorRef.current)[0]?.focus();
      return;
    }
    if (e.key === "Tab") {
      setOpen(false);
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const list = focusableIn(menuRef.current);
    if (!list.length) return;
    const i = list.indexOf(document.activeElement as HTMLElement);
    const next = e.key === "ArrowDown" ? (i + 1) % list.length : (i - 1 + list.length) % list.length;
    list[next].focus();
  };

  return (
    <>
      <span ref={anchorRef} style={{ display: "inline-flex" }}>
        {triggerNode}
      </span>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="menu"
            role="menu"
            onKeyDown={onMenuKey}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              // Right-aligned to the trigger without measuring first.
              transform: "translateX(-100%)",
              zIndex: 150,
            }}
          >
            {items.map((item, i) => (
              <div key={i}>
                {item.sep && <div className="sep" />}
                {item.label !== undefined && (
                  <button
                    role="menuitem"
                    className={item.danger ? "danger" : undefined}
                    disabled={item.disabled}
                    onClick={() => {
                      setOpen(false);
                      item.onSelect?.();
                    }}
                  >
                    {item.label}
                  </button>
                )}
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

// ============================================================ Tabs

export interface TabSpec {
  id: string;
  label: ReactNode;
}

export interface TabsProps {
  tabs: TabSpec[];
  active: string;
  onChange: (id: string) => void;
}

export function Tabs({ tabs, active, onChange }: TabsProps) {
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const i = tabs.findIndex((t) => t.id === active);
    if (i < 0) return;
    const next = e.key === "ArrowRight" ? (i + 1) % tabs.length : (i - 1 + tabs.length) % tabs.length;
    onChange(tabs[next].id);
  };

  return (
    <div className="tabs" role="tablist" onKeyDown={onKey}>
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          id={`tab-${t.id}`}
          aria-selected={t.id === active}
          aria-controls={`panel-${t.id}`}
          tabIndex={t.id === active ? 0 : -1}
          className={`tab${t.id === active ? " active" : ""}`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ============================================================ Toggle

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  /** Required when there is no visible label. */
  ariaLabel?: string;
}

export function Toggle({ checked, onChange, label, disabled, ariaLabel }: ToggleProps) {
  return (
    <label className="row" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
      <span className="switch">
        {/* The slot and knob are siblings AFTER the input: styles.css drives both
            off `input:checked ~ .slot`. */}
        <input
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          aria-label={ariaLabel ?? (typeof label === "string" ? label : "Toggle")}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="slot" />
        <span className="knob" />
      </span>
      {label !== undefined && <span>{label}</span>}
    </label>
  );
}

// ============================================================ Field

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
}

export function Field({ label, hint, error, required, children }: FieldProps) {
  const base = useId();
  const fallbackId = `${base}-input`;
  const hintId = `${base}-hint`;
  const errId = `${base}-err`;

  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(" ");

  let controlId = fallbackId;
  let control: ReactNode = children;
  if (isValidElement(children)) {
    const props = children.props as { id?: string; "aria-describedby"?: string };
    controlId = props.id ?? fallbackId;
    control = cloneElement(children as ReactElement<Record<string, unknown>>, {
      id: controlId,
      "aria-describedby": [props["aria-describedby"], describedBy].filter(Boolean).join(" ") || undefined,
      "aria-invalid": error ? true : undefined,
      "aria-required": required || undefined,
    });
  }

  return (
    <div className={`field${error ? " invalid" : ""}`}>
      <label htmlFor={controlId}>
        {label}
        {required && (
          <span style={{ color: "var(--critical)" }} aria-hidden>
            {" *"}
          </span>
        )}
      </label>
      {control}
      {hint && (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="error" id={errId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

// ============================================================ Search

export interface SearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
}

export function Search({ value, onChange, placeholder = "Search…", ariaLabel }: SearchProps) {
  return (
    <div className="search">
      <span className="icon" aria-hidden>
        ⌕
      </span>
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {value !== "" && (
        <button className="clear" onClick={() => onChange("")} aria-label="Clear search">
          ✕
        </button>
      )}
    </div>
  );
}

// ============================================================ Badge / Tag

export type BadgeTone =
  | "good"
  | "warning"
  | "serious"
  | "critical"
  | "info"
  | "brand"
  | "channel"
  | "neutral";

const STATUS_TONES = new Set<BadgeTone>(["good", "warning", "serious", "critical"]);

export function Badge({
  tone = "neutral",
  children,
  title,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  title?: string;
}) {
  return (
    <span className={`badge${tone === "neutral" ? "" : ` ${tone}`}`} title={title}>
      {/* Status carries a dot AND the word, so state is never colour alone. */}
      {STATUS_TONES.has(tone) && <span className="dot" aria-hidden />}
      {children}
    </span>
  );
}

export function Tag({ label, onRemove }: { label: string; onRemove?: () => void }) {
  return (
    <span className="tag">
      {label}
      {onRemove && (
        <button onClick={onRemove} aria-label={`Remove ${label}`}>
          ✕
        </button>
      )}
    </span>
  );
}

// ============================================================ state views

export function Empty({
  title,
  body,
  icon,
  action,
}: {
  title: ReactNode;
  body?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty" role="status">
      {icon && (
        <div className="icon" aria-hidden style={{ fontSize: 28 }}>
          {icon}
        </div>
      )}
      <h3>{title}</h3>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return <span className="spinner" role="status" aria-label={label} />;
}

/**
 * Placeholder rows for a loading list. Rendered as divs rather than <tr>s so the
 * same component is valid inside a table cell, a card, or a panel.
 */
export function SkeletonRows({ n = 5, cols = 3 }: { n?: number; cols?: number }) {
  return (
    <div className="col" aria-hidden>
      {Array.from({ length: n }, (_, r) => (
        <div className="row" key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <div
              className="skeleton line"
              key={c}
              style={{ flex: c === 0 ? 2 : 1, marginBottom: 0 }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export type BannerTone = "info" | "warning" | "critical" | "good";

const BANNER_ICON: Record<BannerTone, string> = {
  info: "ⓘ",
  warning: "⚠",
  critical: "⚠",
  good: "✓",
};

export function Banner({
  tone = "info",
  traceId,
  children,
  onDismiss,
}: {
  tone?: BannerTone;
  /** Always pass err.traceId on an error banner — it is how support finds the log line. */
  traceId?: string;
  children: ReactNode;
  onDismiss?: () => void;
}) {
  return (
    <div className={`banner ${tone}`} role={tone === "critical" ? "alert" : "status"}>
      <span aria-hidden>{BANNER_ICON[tone]}</span>
      <div className="body">
        {children}
        {traceId && <div className="trace">trace {traceId}</div>}
      </div>
      {traceId && <CopyButton value={traceId} label="Copy trace id" />}
      {onDismiss && (
        <button className="btn icon sm ghost" onClick={onDismiss} aria-label="Dismiss">
          ✕
        </button>
      )}
    </div>
  );
}

// ============================================================ Pager

export interface PagerProps {
  total: number;
  limit: number;
  offset: number;
  /** Receives the new OFFSET. */
  onChange: (offset: number) => void;
  itemName?: string;
}

/** 1, …, 4, 5, 6, …, 20 — one sibling either side, first and last always shown. */
function pageNumbers(current: number, totalPages: number, siblings = 1): (number | "…")[] {
  if (totalPages <= 5 + siblings * 2) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const left = Math.max(2, current - siblings);
  const right = Math.min(totalPages - 1, current + siblings);
  const out: (number | "…")[] = [1];
  if (left > 2) out.push("…");
  for (let p = left; p <= right; p++) out.push(p);
  if (right < totalPages - 1) out.push("…");
  out.push(totalPages);
  return out;
}

export function Pager({ total, limit, offset, onChange, itemName = "items" }: PagerProps) {
  if (total <= 0 || limit <= 0) return null;

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const start = offset + 1;
  const end = Math.min(offset + limit, total);
  const go = (p: number) => onChange((Math.min(Math.max(1, p), totalPages) - 1) * limit);

  return (
    <div className="pager">
      <span>
        Showing {start} to {end} of {total} {itemName}
      </span>
      <span className="spacer" />
      {totalPages > 1 && (
        <>
          <button
            className="btn sm icon"
            onClick={() => go(1)}
            disabled={page === 1}
            aria-label="First page"
          >
            «
          </button>
          <button
            className="btn sm icon"
            onClick={() => go(page - 1)}
            disabled={page === 1}
            aria-label="Previous page"
          >
            ‹
          </button>
          {pageNumbers(page, totalPages).map((p, i) =>
            p === "…" ? (
              <span key={`gap-${i}`} className="muted">
                …
              </span>
            ) : (
              <button
                key={p}
                className={`btn sm${p === page ? " primary" : ""}`}
                onClick={() => go(p)}
                aria-label={`Page ${p}`}
                aria-current={p === page ? "page" : undefined}
              >
                {p}
              </button>
            )
          )}
          <button
            className="btn sm icon"
            onClick={() => go(page + 1)}
            disabled={page === totalPages}
            aria-label="Next page"
          >
            ›
          </button>
          <button
            className="btn sm icon"
            onClick={() => go(totalPages)}
            disabled={page === totalPages}
            aria-label="Last page"
          >
            »
          </button>
        </>
      )}
    </div>
  );
}

// ============================================================ Table

export interface Column<T> {
  /** Field name, or a dotted path for nested payloads ("role.name"). */
  key: string;
  header: ReactNode;
  width?: string;
  align?: "left" | "right";
  sortable?: boolean;
  render?: (row: T) => ReactNode;
}

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  /** A string becomes the empty-state title; a node is rendered as-is. */
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  selectedId?: string | null;
}

function valueAt(row: unknown, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>((acc, k) => (acc == null ? undefined : (acc as Record<string, unknown>)[k]), row);
}

function compare(a: unknown, b: unknown, dir: "asc" | "desc"): number {
  const sign = dir === "asc" ? 1 : -1;
  const aNull = a === null || a === undefined || a === "";
  const bNull = b === null || b === undefined || b === "";
  if (aNull && bNull) return 0;
  // Blanks sort to the bottom in either direction; an operator scanning a
  // sorted column wants the populated rows first.
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === "boolean" || typeof b === "boolean") return sign * ((a ? 1 : 0) - (b ? 1 : 0));
  if (typeof a === "number" && typeof b === "number") return sign * (a - b);
  return sign * String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
}

function defaultCell(v: unknown): ReactNode {
  if (v === null || v === undefined || v === "") return <span className="muted">—</span>;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return <span className="muted">—</span>;
  return String(v);
}

export function Table<T>({ columns, rows, loading, empty, onRowClick, selectedId }: TableProps<T>) {
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortable) return rows;
    return [...rows].sort((a, b) => compare(valueAt(a, sort.key), valueAt(b, sort.key), sort.dir));
  }, [rows, columns, sort]);

  const toggleSort = (key: string) =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

  const rowKey = (row: T, i: number): string => {
    const id = (row as unknown as { id?: unknown })?.id;
    return typeof id === "string" || typeof id === "number" ? String(id) : String(i);
  };

  const emptyNode =
    typeof empty === "string" || empty === undefined ? (
      <Empty title={typeof empty === "string" ? empty : "Nothing to show"} />
    ) : (
      empty
    );

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <tr>
            {columns.map((col) => {
              const active = sort?.key === col.key;
              return (
                <th
                  key={col.key}
                  className={[col.align === "right" ? "num" : "", col.sortable ? "sortable" : ""]
                    .filter(Boolean)
                    .join(" ")}
                  style={col.width ? { width: col.width } : undefined}
                  aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : "none"}
                >
                  {col.sortable ? (
                    <button className="sort" onClick={() => toggleSort(col.key)}>
                      {col.header}
                      <span className={`arrow${active ? " on" : ""}`} aria-hidden>
                        {active ? (sort!.dir === "asc" ? "↑" : "↓") : "↕"}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length}>
                <SkeletonRows n={5} cols={columns.length} />
              </td>
            </tr>
          ) : sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>{emptyNode}</td>
            </tr>
          ) : (
            sorted.map((row, i) => {
              const id = rowKey(row, i);
              return (
                <tr
                  key={id}
                  className={selectedId != null && selectedId === id ? "selected" : undefined}
                  style={onRowClick ? { cursor: "pointer" } : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map((col) => (
                    <td key={col.key} className={col.align === "right" ? "num" : undefined}>
                      {col.render ? col.render(row) : defaultCell(valueAt(row, col.key))}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================ small values

export function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // clipboard API needs a secure context; fall back to the old selection trick
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setDone(true);
    window.setTimeout(() => setDone(false), 1200);
  };

  return (
    <button className="btn icon sm ghost" onClick={copy} aria-label={label} title={label}>
      {done ? "✓" : "⧉"}
    </button>
  );
}

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function relative(d: Date, now: number): string {
  const diff = now - d.getTime();
  const abs = Math.abs(diff);
  const mins = Math.floor(abs / 60_000);
  if (mins < 1) return "just now";
  const suffix = diff >= 0 ? " ago" : " from now";
  if (mins < 60) return `${mins}m${suffix}`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h${suffix}`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d${suffix}`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function RelativeTime({ value }: { value: string | number | Date | null | undefined }) {
  const d = toDate(value);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!d) return;
    const t = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, [d?.getTime()]);

  if (!d) return <span className="muted">—</span>;
  return (
    <time dateTime={d.toISOString()} title={d.toLocaleString()}>
      {relative(d, now)}
    </time>
  );
}

export function Money({
  value,
  currency = "USD",
}: {
  value: number | null | undefined;
  currency?: string;
}) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return <span className="muted">—</span>;
  }
  return (
    <span className="num">
      {new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(value)}
    </span>
  );
}

export function Num({
  value,
  decimals,
  unit,
}: {
  value: number | null | undefined;
  decimals?: number;
  unit?: string;
}) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return <span className="muted">—</span>;
  }
  const text = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals ?? 2,
  }).format(value);
  return (
    <span className="num">
      {text}
      {unit ? ` ${unit}` : ""}
    </span>
  );
}

// ============================================================ toasts

export type ToastTone = "good" | "critical" | "warning" | "info" | "neutral";

interface ToastRecord {
  id: number;
  message: ReactNode;
  tone: ToastTone;
}

let toastSeq = 0;
let toastList: ToastRecord[] = [];
const toastSubs = new Set<(list: ToastRecord[]) => void>();

function publishToasts() {
  const snapshot = toastList.slice();
  for (const fn of toastSubs) fn(snapshot);
}

function dismissToast(id: number) {
  toastList = toastList.filter((t) => t.id !== id);
  publishToasts();
}

/** Module-level so a push works from anywhere, including outside the React tree. */
export function pushToast(message: ReactNode, tone: ToastTone = "neutral"): number {
  const id = ++toastSeq;
  toastList = [...toastList, { id, message, tone }];
  publishToasts();
  // A failure stays up longer: the operator has to read it before acting.
  window.setTimeout(() => dismissToast(id), tone === "critical" ? 8000 : 4500);
  return id;
}

interface ToastApi {
  push: (message: ReactNode, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastApi>({ push: pushToast });

export function ToastProvider({ children }: { children: ReactNode }) {
  const value = useMemo<ToastApi>(() => ({ push: pushToast }), []);
  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastApi {
  return useContext(ToastContext);
}

/** Mount once, in the shell. */
export function ToastHost() {
  const [list, setList] = useState<ToastRecord[]>(toastList);
  const reduce = useReducedMotion();

  useEffect(() => {
    toastSubs.add(setList);
    setList(toastList);
    return () => {
      toastSubs.delete(setList);
    };
  }, []);

  return createPortal(
    <div className="toasts" aria-live="polite" aria-atomic="false">
      <AnimatePresence initial={false}>
        {list.map((t) => (
          <motion.div
            key={t.id}
            className={`toast${t.tone === "neutral" ? "" : ` ${t.tone}`}`}
            role={t.tone === "critical" ? "alert" : "status"}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
          >
            <div className="row">
              <div className="body">{t.message}</div>
              <button
                className="btn icon sm ghost"
                onClick={() => dismissToast(t.id)}
                aria-label="Dismiss notification"
              >
                ✕
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
}

// ============================================================ useAsync

export interface AsyncState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Every consumer reads `error.traceId`, so a non-ApiError (a TypeError from a
 * dropped connection, say) is wrapped rather than passed through — a screen
 * should never have to type-test before it can render its error state.
 */
function asApiError(e: unknown): ApiError {
  if (e instanceof ApiError) return e;
  const message = e instanceof Error ? e.message : String(e);
  return new ApiError(0, "client_error", message, "");
}

/** The standard fetch hook: runs on mount and whenever `deps` change. */
export function useAsync<T>(fn: () => Promise<T>, deps: DependencyList = []): AsyncState<T> {
  const [state, setState] = useState<{ data: T | null; error: ApiError | null; loading: boolean }>({
    data: null,
    error: null,
    loading: true,
  });

  const fnRef = useRef(fn);
  fnRef.current = fn;

  // A stale response must never overwrite a newer one, and nothing may be set
  // after unmount — hence both the generation counter and the alive flag.
  const generation = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const run = useCallback(() => {
    const mine = ++generation.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fnRef.current().then(
      (data) => {
        if (alive.current && mine === generation.current) setState({ data, error: null, loading: false });
      },
      (err: unknown) => {
        if (alive.current && mine === generation.current) {
          setState({ data: null, error: asApiError(err), loading: false });
        }
      }
    );
  }, []);

  useEffect(() => {
    run();
    // Callers own the dependency list; `run` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ...state, reload: run };
}
